import { useMemo } from 'react';
import type { PassengerDemandView } from '../../../domain/models/contracts';
import { buildLeakageSlices } from './routeMetrics';
import { LEAKAGE_COLORS } from './chartTheme';
import { formatInt, formatPct } from './formatters';
import { InfoPopover } from '../common/InfoPopover';

interface LeakageBreakdownProps {
  readonly demand: PassengerDemandView;
}

export function LeakageBreakdown({ demand }: LeakageBreakdownProps) {
  const slices = useMemo(() => buildLeakageSlices(demand), [demand]);
  const totalLost = slices.reduce((sum, slice) => sum + slice.passengers, 0);
  const dominant = slices.reduce((best, slice) =>
    slice.passengers > best.passengers ? slice : best,
  );

  return (
    <section className="ri-panel" aria-label="Spill and leakage diagnostics">
      <header className="ri-panel__header">
        <h2 className="ri-panel__title">Spill &amp; Leakage</h2>
        <p className="ri-panel__subtitle">
          Why {formatInt(totalLost)} passengers were not carried
        </p>
      </header>

      {totalLost <= 0 ? (
        <p className="ri-empty">No lost demand recorded. Every potential passenger was carried.</p>
      ) : (
        <>
          {/* Stacked overview bar */}
          <div
            className="ri-stack"
            role="img"
            aria-label={slices
              .map((slice) => `${slice.label} ${formatPct(slice.sharePct, 0)}`)
              .join(', ')}
          >
            {slices.map((slice) =>
              slice.sharePct > 0 ? (
                <span
                  key={slice.key}
                  className="ri-stack__segment"
                  style={{ width: `${slice.sharePct}%`, backgroundColor: LEAKAGE_COLORS[slice.key] }}
                  title={`${slice.label}: ${formatInt(slice.passengers)}`}
                />
              ) : null,
            )}
          </div>

          <ul className="ri-leaks">
            {slices.map((slice) => (
              <li key={slice.key} className="ri-leak">
                <div className="ri-leak__head">
                  <span className="ri-leak__name">
                    <span
                      className="ri-leak__swatch"
                      style={{ backgroundColor: LEAKAGE_COLORS[slice.key] }}
                      aria-hidden="true"
                    />
                    {slice.label}
                    <InfoPopover topic={slice.key} />
                  </span>
                  <span className="ri-leak__figures">
                    {formatInt(slice.passengers)} pax · {formatPct(slice.sharePct, 0)} of loss ·{' '}
                    {formatPct(slice.ofPotentialPct)} of potential
                  </span>
                </div>
                <div
                  className="ri-bar"
                  role="progressbar"
                  aria-label={`${slice.label} share of lost demand`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(slice.sharePct)}
                >
                  <div
                    className="ri-bar__fill"
                    style={{ width: `${slice.sharePct}%`, backgroundColor: LEAKAGE_COLORS[slice.key] }}
                  />
                </div>
                <p className="ri-leak__desc">{slice.description}</p>
              </li>
            ))}
          </ul>

          <p className="ri-callout">
            <strong>Biggest leak: {dominant.label}.</strong> {dominant.lever}
          </p>
        </>
      )}
    </section>
  );
}
